import Utils from '~/scripts/services/utils';

/**
 * Escapes a value for CSV output.
 *
 * @param {any} value Value to escape
 * @returns {string} Escaped value
 */
function escapeCSV(value) {
	const text = value === null || value === undefined ? '' : String(value);
	if (/[\n";]/.test(text)) {
		return `"${text.replaceAll('"', '""')}"`;
	}
	return text;
}

/**
 * Builds a CSV string from rows restricted to the given columns.
 *
 * @param {any[]} rows Rows data
 * @param {{ field: string, title: string }[]} columns Columns to include
 * @returns {string} CSV content
 */
function toCSV(rows, columns) {
	const header = columns.map(column => escapeCSV(column.title)).join(';');
	const lines = rows.map(row => columns.map(column => escapeCSV(row[column.field])).join(';'));

	return [header, ...lines].join('\n');
}

/**
 * Builds a JSON string from rows restricted to the given columns.
 *
 * @param {any[]} rows Rows data
 * @param {{ field: string, title: string }[]} columns Columns to include
 * @returns {string} JSON content
 */
function toJSON(rows, columns) {
	const data = rows.map(row => {
		/** @type {Record<string, any>} */
		const item = {};
		for (const column of columns) {
			item[column.field] = row[column.field] ?? null;
		}
		return item;
	});

	return JSON.stringify(data, null, 2);
}

export default class ModalListExportController {
	/* @ngInject */
	/**
	 * @param {import('angular').ui.bootstrap.IModalInstanceService} $uibModalInstance
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {(value: string, showNotification: boolean) => boolean} $copyToClipboard
	 * @param {any[]} selection Selected rows data
	 * @param {{ field: string, title: string }[]} columns Available columns
	 * @param {boolean} hasSelection Whether a selection exists
	 */
	constructor($uibModalInstance, $translate, $copyToClipboard, selection, columns, hasSelection) {
		this.$uibModalInstance = $uibModalInstance;
		this.$translate = $translate;
		this.$copyToClipboard = $copyToClipboard;

		/** @type {any[]} */
		this.selection = selection || [];

		/** @type {{ field: string, title: string, selected: boolean }[]} */
		this.columns = (columns || []).map(column => ({ ...column, selected: true }));

		/** @type {boolean} */
		this.hasSelection = hasSelection;

		/** @type {'all' | 'selection'} */
		this.scope = hasSelection ? 'selection' : 'all';

		/** @type {'xlsx' | 'csv' | 'json'} */
		this.format = 'xlsx';

		/** @type {Record<string, { title: string, enabled: boolean }>} */
		this.availableFormats = {
			xlsx: {
				title: $translate.instant('list.export-xlsx'),
				enabled: true,
			},
			csv: {
				title: $translate.instant('list.export-csv'),
				enabled: true,
			},
			json: {
				title: $translate.instant('list.export-json'),
				enabled: true,
			}
		}
	}

	/**
	 * Whether a given export format is disabled for the current scope.
	 * CSV and JSON are only available when exporting a selection.
	 *
	 * @param {string} key Format key (xlsx, csv, json)
	 * @returns {boolean} True when the format must be disabled
	 */
	isFormatDisabled(key) {
		if (!this.availableFormats[key].enabled) {
			return true;
		}
		return this.scope === 'all' && (key === 'csv' || key === 'json');
	}

	/**
	 * Resets the selected format to a valid one when the current one becomes disabled
	 * after a scope change.
	 */
	onScopeChange() {
		if (this.isFormatDisabled(this.format)) {
			this.format = 'xlsx';
		}
	}

	/**
	 * Returns the columns selected by the user.
	 *
	 * @returns {{ field: string, title: string }[]} Selected columns
	 */
	getSelectedColumns() {
		return this.columns.filter(column => column.selected);
	}

	/**
	 * Whether the export can be performed client-side (no server call).
	 * Only a selection exported as CSV or JSON can be built directly in the browser.
	 *
	 * @returns {boolean} True when a selection is exported as csv/json
	 */
	canExportClientSide() {
		return this.scope === 'selection' && (this.format === 'csv' || this.format === 'json');
	}

	/**
	 * Whether the "copy to clipboard" action is available.
	 *
	 * @returns {boolean} True when a selection is exported as csv/json
	 */
	canCopy() {
		return this.canExportClientSide();
	}

	/**
	 * Builds the export content from the current selection and the selected columns.
	 *
	 * @returns {{ content: string, mime: string, extension: string }} Export payload
	 */
	buildContent() {
		const columns = this.getSelectedColumns();
		if (this.format === 'json') {
			return { content: toJSON(this.selection, columns), mime: 'application/json', extension: 'json' };
		}
		return { content: toCSV(this.selection, columns), mime: 'text/csv;charset=utf-8', extension: 'csv' };
	}

	/**
	 * Builds the export content from the current selection and copies it to the clipboard.
	 */
	copyToClipboard() {
		const { content } = this.buildContent();

		this.$copyToClipboard(content, true);
		this.$uibModalInstance.close();
	}

	/**
	 * Builds the export content from the current selection and downloads it client-side,
	 * without any server call.
	 */
	downloadClientSide() {
		const { content, mime, extension } = this.buildContent();

		Utils.download(content, mime, `export.${extension}`);
		this.$uibModalInstance.close();
	}

	cancel() {
		return this.$uibModalInstance.dismiss();
	}

	submit() {
		// Selection exported as CSV/JSON is built and downloaded directly in the browser,
		// the server export (Extract API) is only used for the other cases (xlsx / full scope).
		if (this.canExportClientSide()) {
			this.downloadClientSide();
			return undefined;
		}

		return this.$uibModalInstance.close({
			scope:   this.scope,
			format:  this.format,
			columns: this.getSelectedColumns(),
		});
	}
}
